import React, { useRef, useEffect } from 'react';

// ═══════════════════════════════════════════════
// SimCompareChart — 多次模拟结果并列对比 (Canvas 柱状图)
// ═══════════════════════════════════════════════

const COLORS = {
  bg: '#FFFFFF', grid: 'rgba(30,42,58,0.08)',
  text: '#1E2A3A', textSec: '#4F6680', textMuted: '#8598AD',
  border: '#D6DAE0',
  L: '#C2533A', w0: '#2874A6', eff: '#1E8449', score: '#6A4C93',
};
const FONT = '"Microsoft YaHei","PingFang SC",sans-serif';
const FONT_MONO = '"Cascadia Code",Consolas,monospace';

export type SimSnapshot = {
  id: number;
  label: string;
  half_length: number;
  avg_width: number;
  avg_efficiency: number;
  frac_height?: number;
  effectiveness_score?: number;
};

type Props = {
  sims: SimSnapshot[];
  height?: number;
};

type MetricDef = {
  key: keyof SimSnapshot;
  label: string;
  unit: string;
  color: string;
};

const METRICS: MetricDef[] = [
  { key: 'half_length', label: '总半缝长', unit: 'm', color: COLORS.L },
  { key: 'avg_width', label: '平均缝宽', unit: 'mm', color: COLORS.w0 },
  { key: 'avg_efficiency', label: '液效', unit: '%', color: COLORS.eff },
  { key: 'effectiveness_score', label: '有效性', unit: '分', color: COLORS.score },
];

export default function SimCompareChart({ sims, height = 280 }: Props) {
  const ref = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const cv = ref.current;
    if (!cv || sims.length < 2) return;
    const ctx = cv.getContext('2d')!;
    const dpr = window.devicePixelRatio || 2;
    const rect = cv.getBoundingClientRect();
    cv.width = rect.width * dpr;
    cv.height = rect.height * dpr;
    ctx.scale(dpr, dpr);
    const W = rect.width, H = rect.height;

    ctx.fillStyle = COLORS.bg;
    ctx.fillRect(0, 0, W, H);

    // 分组柱状图布局: 每组 3 根柱子 (L/w0/eff) × N 次模拟
    const nSims = sims.length;
    const nMetrics = METRICS.length;
    const metricScales = METRICS.map((metric) => Math.max(
      1,
      ...sims.map((simulation) => Number(simulation[metric.key]) || 0),
    ) * 1.12);
    const groupW = (W - 60) / nSims;
    const barW = Math.max(4, (groupW - 16) / nMetrics);
    const gap = 6;

    // 网格
    ctx.strokeStyle = COLORS.grid;
    ctx.lineWidth = 0.5;
    for (let k = 0; k <= 4; k++) {
      const y = 20 + (k / 4) * (H - 60);
      ctx.beginPath(); ctx.moveTo(50, y); ctx.lineTo(W - 12, y); ctx.stroke();
    }

    // 每组柱子
    sims.forEach((sim, gi) => {
      const gx = 50 + gi * groupW;
      METRICS.forEach((m, mi) => {
        const val = Number(sim[m.key]) || 0;
        const ratio = Math.min(1, val / metricScales[mi]);
        const bh = ratio * (H - 60);
        const bx = gx + 8 + mi * (barW + gap);
        const by = H - 28 - bh;

        // 柱体
        ctx.fillStyle = m.color + 'CC';
        ctx.fillRect(bx, by, barW, bh);

        // 柱顶数值
        if (bh > 12) {
          ctx.fillStyle = m.color;
          ctx.font = `bold 9px ${FONT_MONO}`;
          ctx.textAlign = 'center';
          ctx.fillText(val.toFixed(1), bx + barW / 2, by - 3);
        }
      });

      // 组标签
      ctx.fillStyle = COLORS.textSec;
      ctx.font = `10px ${FONT}`;
      ctx.textAlign = 'center';
      ctx.fillText(sim.label, gx + groupW / 2, H - 8);
    });

    // 图例
    ctx.font = `9px ${FONT}`;
    let lx = 54;
    METRICS.forEach((m) => {
      ctx.fillStyle = m.color;
      ctx.fillRect(lx, 6, 10, 3);
      ctx.fillStyle = COLORS.textSec;
      ctx.textAlign = 'left';
      ctx.fillText(`${m.label}(${m.unit})`, lx + 13, 12);
      lx += 78;
    });
  }, [sims]);

  if (sims.length < 2) {
    return (
      <div style={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center', color: COLORS.textMuted, fontSize: 12 }}>
        需要 ≥2 次模拟结果才能对比
      </div>
    );
  }

  return <canvas ref={ref} style={{ width: '100%', height, display: 'block' }} />;
}
